import React, { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'

import Image from '../atoms/Image'

/**
 * Listのid
 */
const LIST_ID = 'ImageList__List'

/**
 * Itemのid prefix
 */
const ITEM_ID_PREFIX = 'ImageList__Item'

const Container = styled.div`
  position: relative;
`

const Prev = styled.div<{ show: boolean }>`
  cursor: pointer;
  display: ${({ show }) => (show ? 'block' : 'none')};
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    display: none;
  }
`

const List = styled.ul`
  display: flex;
  list-style: none;
  min-width: 100%;
  overflow-x: scroll;
  scroll-behavior: smooth;
`

const Next = styled.div<{ show: boolean }>`
  cursor: pointer;
  display: ${({ show }) => (show ? 'block' : 'none')};
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    display: none;
  }
`

const Item = styled.li`
  min-width: 100%;
  padding: 0 10px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    min-width: 50%;
    padding: 0 10px 0;
    &:first-child {
      padding-left: 0;
    }
    &:nth-child(n + 2) {
      padding-right: 0;
    }
  }
`

const ItemLink = styled(Link)`
  display: block;
`

const Navigation = styled.ul`
  align-items: center;
  display: flex;
  justify-content: center;
  list-style: none;
  margin: 10px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const NavigationItem = styled.li<{ active: boolean }>`
  color: ${({ active, theme }) => (active ? theme.palette.primary.light : theme.palette.background.gray)};
  font-size: 12px;
  margin: 0 4px 0;
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
`

type Props = {
  className?: string
  images: {
    alt?: string
    filename: string
    to: string
  }[]
}

export default function ImageList({ className, images }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)

  return (
    <Container>
      <List
        className={className}
        id={LIST_ID}
        onScroll={() => {
          const element = document.querySelector(`#${LIST_ID}`) as HTMLElement
          const width = element.scrollWidth / images.length
          const widthHalf = Math.ceil(width / 2)
          const thresholds = [...Array(images.length)].map((_, index) => widthHalf + width * index)
          const index = thresholds.findIndex(threshold => element.scrollLeft < threshold)

          if (-1 < index && activeIndex !== index) {
            setActiveIndex(index)
          }
        }}
      >
        {images.map(({ alt, filename, to }, i) => (
          <Item id={`${ITEM_ID_PREFIX}-${i}`} key={i}>
            <ItemLink to={to}>
              <Image alt={alt} filename={filename} />
            </ItemLink>
          </Item>
        ))}
      </List>

      <Prev
        onClick={() => {
          const element = document.querySelector(`#${LIST_ID}`) as HTMLElement
          const width = element.scrollWidth / images.length
          element.scrollLeft = width * (activeIndex - 1)
        }}
        show={activeIndex > 0}
      >
        <Image alt="戻る" filename="icon-prev.png" />
      </Prev>
      <Next
        onClick={() => {
          const element = document.querySelector(`#${LIST_ID}`) as HTMLElement
          const width = element.scrollWidth / images.length
          element.scrollLeft = width * (activeIndex + 1)
        }}
        show={activeIndex < images.length - 2}
      >
        <Image alt="進む" filename="icon-next.png" />
      </Next>
      {images.length >= 2 && (
        <Navigation>
          {images.map((_, i) => (
            <NavigationItem
              active={activeIndex === i}
              key={i}
              onClick={() => {
                const width = document.querySelector(`#${ITEM_ID_PREFIX}-${i}`)!.clientWidth
                document.querySelector(`#${LIST_ID}`)!.scrollLeft = width * i
              }}
            >
              &#x25CF;
            </NavigationItem>
          ))}
        </Navigation>
      )}
    </Container>
  )
}
